'use client';

import Link from 'next/link';
import { Home, ShoppingCart, User, Heart } from 'lucide-react';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/auth-context';
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { TruecallerLoginModal } from '@/components/auth/truecaller-login-modal';

export function BottomBar() {
  const pathname = usePathname();
  const router = useRouter();
  const { customer } = useAuth();
  const WHATSAPP_LINK = "https://wa.me/919742221600"; // Updated WhatsApp number
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Fetch cart items for count - must be called before any early returns
  const { data: cartData } = useQuery({
    queryKey: ["/api/cart"],
    queryFn: async () => {
      const response = await fetch('/api/cart', {
        credentials: 'include' // ✅ Include cookies in request
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch cart: ${response.status}`);
      }
      return response.json();
    },
    enabled: !!customer,
    retry: (failureCount, error) => {
      // Don't retry on 401 (unauthorized) errors
      if (error?.message?.includes('401')) {
        return false;
      }
      return failureCount < 2; // Less retries for mobile nav
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
  });

  // Extract cart items from response
  const cartItems = cartData?.items || [];
  const safeCartItems = Array.isArray(cartItems) ? cartItems : [];
  const cartItemCount = safeCartItems.reduce((sum, item) => sum + (item.quantity || 0), 0);

  // Scroll detection for hiding/showing bottom bar
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Show bar when scrolling up or at top, hide when scrolling down
      if (currentScrollY < lastScrollY || currentScrollY < 10) {
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  // Hide bottom bar on login, product, cart and checkout pages for better UX - after all hooks are called
  if (pathname === '/login' || pathname.startsWith('/product/') || pathname === '/cart' || pathname === '/checkout') {
    return null;
  }

  const navItems = [
    { href: '/', icon: Home, label: 'Home' },
    { href: '/wishlist', icon: Heart, label: 'Wishlist', requiresAuth: !customer },
    { href: '/cart', icon: ShoppingCart, label: 'Cart' },
    { href: customer ? '/profile' : '#', icon: User, label: 'Profile', requiresAuth: !customer },
  ];

  return (
    <div className={`fixed bottom-0 left-0 right-0 z-50 sm:hidden transition-transform duration-300 ${isVisible ? 'translate-y-0' : 'translate-y-full'}`}>
      {/* Background filler to hide details behind/below the bar */}
      <div className="absolute inset-0 bg-white shadow-[0_-4px_20px_-5px_rgba(0,0,0,0.1)]" />

      <nav
        className="relative h-[68px] pb-3 bg-white border-t border-gray-100"
        aria-label="Mobile navigation"
      >
        <div className="grid h-full max-w-lg grid-cols-4 mx-auto font-medium">
          {navItems.map((item) => {
            const isActive = pathname === item.href;
            const Icon = item.icon;
            const isCartItem = item.label === 'Cart';

            // Handle Profile button click for non-authenticated users
            if (item.requiresAuth) {
              return (
                <button
                  key={item.label}
                  onClick={() => setShowLoginModal(true)}
                  className={`inline-flex flex-col items-center justify-center px-1 pt-1.5 pb-1 text-center group transition-colors duration-300 relative
                    ${isActive ? 'text-orange-600' : 'text-gray-500 hover:text-orange-500'}
                  `}
                >
                  <div
                    className={`w-16 h-8 flex items-center justify-center rounded-full transition-all duration-300 relative
                    ${isActive ? 'bg-orange-100' : 'bg-transparent'}`
                    }
                  >
                    <Icon
                      className="w-6 h-6"
                      strokeWidth={isActive ? 2 : 1.5}
                    />
                  </div>
                  <span className="text-[13px] font-medium mt-0">{item.label}</span>
                </button>
              );
            }

            return (
              <Link
                key={item.label}
                href={item.href}
                className={`inline-flex flex-col items-center justify-center px-1 pt-1.5 pb-1 text-center group transition-colors duration-300 relative
                  ${isActive ? 'text-orange-600' : 'text-gray-500 hover:text-orange-500'}
                `}
              >
                <div
                  className={`w-16 h-8 flex items-center justify-center rounded-full transition-all duration-300 relative
                  ${isActive ? 'bg-orange-100' : 'bg-transparent'}`
                  }
                >
                  <Icon
                    className="w-6 h-6"
                    strokeWidth={isActive ? 2 : 1.5}
                  />
                  {/* Cart count badge */}
                  {isCartItem && cartItemCount > 0 && (
                    <span className="absolute -top-1 -right-0.5 bg-orange-600 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 font-bold z-10">
                      {cartItemCount > 99 ? '99+' : cartItemCount}
                    </span>
                  )}
                </div>
                <span className="text-[13px] font-medium mt-0">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Login Modal for mobile */}
      <TruecallerLoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        returnUrl={pathname}
        onSuccess={() => {
          setShowLoginModal(false);
          router.push('/profile');
        }}
      />
    </div>
  );
} 